import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub, User} from 'yougile-model'
import {Sync} from '@client/sync'
import {AvatarGroup} from '@components/common/base/avatar'
import {
  TableUserInfo,
  UsersTable
} from '@components/common/users-table/users-table'
import {UsersTableUtil} from '@components/common/users-table/users-table-util'
import {TwTitleSecondary} from '@components/task-templates/task-templates-edit'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'
import {UserAccess} from '@extensions/user-access'

const TwAvatarContainer = tw.div`
  flex
  items-center
  gap-16
  h-28
  mb-4
`

interface Props {
  hub?: Hub
  project?: Hub
}

export const TemplateChatSubscribers = (props: Props) => {
  const {hub, project} = props
  const [selectedIds, setIds] = useState(
    () => hub?.data?.taskTemplate?.subscribeUsers || []
  )

  const forceUpdate = useForceUpdate()

  useSyncEvent(
    'current',
    {filter: project?.id},
    () => {
      forceUpdate()
    },
    [project?.id]
  )

  const setIdsHandler = (ids: DataObjectId[]) => {
    setIds(ids)
    taskPhantomObserver.update()
  }

  useEffect(() => {
    if (!hub?.data.taskTemplate) {
      return
    }

    const taskSubscribers = hub?.data?.taskTemplate.subscribeUsers || []
    if (
      taskSubscribers.length !== selectedIds.length ||
      taskSubscribers.some(s => !selectedIds.includes(s))
    ) {
      hub.data.taskTemplate.subscribeUsers = selectedIds
    }
  }, [selectedIds, hub])

  useEffect(() => {
    setIdsHandler(hub?.data?.taskTemplate?.subscribeUsers || [])
  }, [hub?.id])

  const tableUsers = getTableUsers(hub, selectedIds)

  return (
    <TemplateChatSubscribersBase
      selectedIds={selectedIds}
      setSelectedIds={setIdsHandler}
      users={tableUsers}
    />
  )
}

interface PropsBase {
  selectedIds: DataObjectId[]
  setSelectedIds: (value: DataObjectId[]) => void
  users: TableUserInfo[]
}

export const TemplateChatSubscribersBase = (props: PropsBase) => {
  const {selectedIds, setSelectedIds, users} = props

  return (
    <div>
      <TwAvatarContainer>
        <TwTitleSecondary $noGap>
          {Lang.msg.taskTemplates.subscribers}
        </TwTitleSecondary>

        <AvatarGroup
          userIds={selectedIds}
          size={20}
          outlineColor="grey-l80"
          hideStatus
        />
      </TwAvatarContainer>
      <UsersTable
        users={users}
        showPositionColumn={true}
        useCheckbox={true}
        selected={[...selectedIds]}
        onSelectedChanged={setSelectedIds}
        noScroll
        noPaddings
      />
    </div>
  )
}

const getTableUsers = (hub?: Hub, selectedIds: DataObjectId[] = []) => {
  if (!hub) {
    return []
  }

  const box = Sync.getCurrentSandbox()
  const projectId =
    hub?.data?.taskTemplate?.boardId &&
    box.getHub(hub.data.taskTemplate.boardId)?.location
  const project = projectId && box.getHub(projectId)
  const users = project ? UserAccess.listUsersInProject(project) : []
  const selectedUsers = box
    .getAllUsers(false)
    .filter(u => selectedIds.includes(u.id))

  const usersToShow: User[] = [...users]

  selectedUsers.forEach(sel => {
    if (!usersToShow.find(u => sel.id === u.id)) {
      usersToShow.push(sel)
    }
  })

  return usersToShow?.map(u => UsersTableUtil.convertUser(box, u)) || []
}
